import {
  SEAT_CLASS_SPECS,
  type AircraftType,
  type CabinConfiguration,
  type CabinIssueCode,
  type CabinIssueSeverity,
  type CabinMetrics,
  type CabinValidationIssue,
  type CabinValidationResult,
} from '../models';
import {
  CERTIFIED_NEAR_LIMIT_RATIO,
  EXCESS_GALLEY_MARGIN,
  FLOOR_NEARLY_FULL_RATIO,
  FLOOR_UNDERUSED_RATIO,
  FLOOR_UNIT_EPSILON,
  TIGHT_ECONOMY_PITCH_INCHES,
} from './cabinConstants';
import {
  calculateCabinWeight,
  calculateComfortByClass,
  calculateFuelBurn,
  calculateRecommendedGalleys,
  calculateRequiredCabinCrew,
  calculateToleranceByClass,
  getClassRatios,
  getFloorUnitsByClass,
  getFloorUnitsUsed,
  getSeatsByClass,
  getTotalSeats,
  roundTo,
  sanitizeCount,
  seatWeightedAverage,
  buildClassMap,
} from './cabinMath';

/* -------------------------------------------------------------------------- *
 * Validation
 * -------------------------------------------------------------------------- */

const SEVERITY_RANK: Readonly<Record<CabinIssueSeverity, number>> = {
  ERROR: 0,
  WARNING: 1,
  INFO: 2,
};

type NumericField =
  | 'economySeats'
  | 'economyPitchInches'
  | 'premiumEconomySeats'
  | 'businessSeats'
  | 'businessPitchInches'
  | 'firstSeats'
  | 'galleyCount';

const NUMERIC_FIELDS: readonly NumericField[] = [
  'economySeats',
  'economyPitchInches',
  'premiumEconomySeats',
  'businessSeats',
  'businessPitchInches',
  'firstSeats',
  'galleyCount',
];

const SEAT_COUNT_FIELDS: readonly NumericField[] = [
  'economySeats',
  'premiumEconomySeats',
  'businessSeats',
  'firstSeats',
];

function fixed(value: number, decimals = 1): string {
  return value.toFixed(decimals);
}

function percent(ratio: number): string {
  return `${Math.round(ratio * 100)}%`;
}

/** Collects issues and builds the final ordered, counted result. */
class IssueCollector {
  private readonly issues: CabinValidationIssue[] = [];

  add(
    code: CabinIssueCode,
    severity: CabinIssueSeverity,
    message: string,
    field: keyof CabinConfiguration | null
  ): void {
    this.issues.push({ code, severity, message, field });
  }

  ordered(): readonly CabinValidationIssue[] {
    // Array.prototype.sort is stable, so insertion order is kept within a severity.
    return [...this.issues].sort(
      (a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity]
    );
  }

  count(severity: CabinIssueSeverity): number {
    return this.issues.filter((issue) => issue.severity === severity).length;
  }
}

function buildResult(
  type: AircraftType,
  collector: IssueCollector,
  figures: Pick<
    CabinValidationResult,
    | 'floorUnitsUsed'
    | 'totalSeats'
    | 'requiredCabinCrew'
    | 'recommendedGalleys'
  >
): CabinValidationResult {
  const errorCount = collector.count('ERROR');
  return {
    aircraftTypeId: type.id,
    isValid: errorCount === 0,
    issues: collector.ordered(),
    errorCount,
    warningCount: collector.count('WARNING'),
    infoCount: collector.count('INFO'),
    floorUnitsBudget: type.cabinFloorUnits,
    certifiedSeatLimit: type.maxCertifiedSeats,
    ...figures,
  };
}

/**
 * Validates a cabin layout against an aircraft type.
 *
 * ERROR   — the layout cannot be fitted or certified; it must be changed.
 * WARNING — legal but likely a mistake or a hit to service quality.
 * INFO    — context for the player (crew needed, how full the cabin is).
 */
export function validateCabinConfiguration(
  type: AircraftType,
  config: CabinConfiguration
): CabinValidationResult {
  const collector = new IssueCollector();

  // 1. Every number must be finite; otherwise nothing else is meaningful.
  const invalid = NUMERIC_FIELDS.filter((field) => !Number.isFinite(config[field]));
  for (const field of invalid) {
    collector.add('INVALID_NUMBER', 'ERROR', `${field} must be a finite number.`, field);
  }
  if (invalid.length > 0) {
    return buildResult(type, collector, {
      floorUnitsUsed: 0,
      totalSeats: 0,
      requiredCabinCrew: 0,
      recommendedGalleys: 0,
    });
  }

  // 2. Counts must be whole, non-negative numbers.
  for (const field of SEAT_COUNT_FIELDS) {
    const value = config[field];
    if (value < 0) {
      collector.add('NEGATIVE_SEAT_COUNT', 'ERROR', `${field} cannot be negative (${value}).`, field);
    } else if (!Number.isInteger(value)) {
      collector.add('NON_INTEGER_SEAT_COUNT', 'ERROR', `${field} must be a whole number (${value}).`, field);
    }
  }
  if (config.galleyCount < 0 || !Number.isInteger(config.galleyCount)) {
    collector.add(
      'INVALID_GALLEY_COUNT',
      'ERROR',
      `galleyCount must be a whole number of 0 or more (${config.galleyCount}).`,
      'galleyCount'
    );
  }

  const seats = getSeatsByClass(config);
  const totalSeats = getTotalSeats(config);
  const floorUnitsUsed = getFloorUnitsUsed(config);
  const budget = type.cabinFloorUnits;
  const recommendedGalleys = calculateRecommendedGalleys(config);
  const requiredCabinCrew = calculateRequiredCabinCrew(type, totalSeats);

  // 3. Seats exist.
  if (totalSeats <= 0) {
    collector.add('NO_SEATS_INSTALLED', 'ERROR', 'The cabin has no seats installed.', null);
  }

  // 4. Floor unit budget.
  const usageRatio = budget > 0 ? floorUnitsUsed / budget : 0;
  if (floorUnitsUsed > budget + FLOOR_UNIT_EPSILON) {
    collector.add(
      'FLOOR_BUDGET_EXCEEDED',
      'ERROR',
      `The cabin needs ${fixed(floorUnitsUsed)} floor units but the ${type.id} has ${fixed(budget, 0)} ` +
        `(${fixed(floorUnitsUsed - budget)} over budget).`,
      null
    );
  } else if (totalSeats > 0 && usageRatio >= FLOOR_NEARLY_FULL_RATIO) {
    collector.add(
      'FLOOR_BUDGET_NEARLY_FULL',
      'INFO',
      `Cabin floor is ${percent(usageRatio)} full; there is almost no room left to add seats.`,
      null
    );
  } else if (totalSeats > 0 && usageRatio < FLOOR_UNDERUSED_RATIO) {
    collector.add(
      'FLOOR_BUDGET_UNDERUSED',
      'INFO',
      `Only ${percent(usageRatio)} of the cabin floor is used; ` +
        `${fixed(budget - floorUnitsUsed)} floor units are free for more seats or comfort.`,
      null
    );
  }

  // 5. Certified maximum passenger capacity (exit and door limit).
  const certified = type.maxCertifiedSeats;
  if (totalSeats > certified) {
    collector.add(
      'CERTIFIED_SEATS_EXCEEDED',
      'ERROR',
      `${totalSeats} seats exceed the ${certified}-passenger exit and door limit certified for the ${type.id} ` +
        `(${totalSeats - certified} too many).`,
      null
    );
  } else if (certified > 0 && totalSeats / certified >= CERTIFIED_NEAR_LIMIT_RATIO) {
    collector.add(
      'CERTIFIED_SEATS_NEAR_LIMIT',
      'INFO',
      `${totalSeats} seats is ${percent(totalSeats / certified)} of the ${certified}-passenger certified limit.`,
      null
    );
  }

  // 6. Seat pitch bounds, only for classes that have seats installed.
  const economySpec = SEAT_CLASS_SPECS.ECONOMY;
  if (seats.ECONOMY > 0) {
    const pitch = config.economyPitchInches;
    if (pitch < economySpec.minPitchInches || pitch > economySpec.maxPitchInches) {
      collector.add(
        'PITCH_OUT_OF_RANGE',
        'ERROR',
        `Economy pitch ${pitch}" is outside the allowed ${economySpec.minPitchInches}"–${economySpec.maxPitchInches}".`,
        'economyPitchInches'
      );
    } else if (pitch < TIGHT_ECONOMY_PITCH_INCHES) {
      collector.add(
        'TIGHT_ECONOMY_PITCH',
        'WARNING',
        `Economy pitch of ${pitch}" is very tight and will hurt comfort and what passengers will pay.`,
        'economyPitchInches'
      );
    }
  }
  const businessSpec = SEAT_CLASS_SPECS.BUSINESS;
  if (seats.BUSINESS > 0) {
    const pitch = config.businessPitchInches;
    if (pitch < businessSpec.minPitchInches || pitch > businessSpec.maxPitchInches) {
      collector.add(
        'PITCH_OUT_OF_RANGE',
        'ERROR',
        `Business pitch ${pitch}" is outside the allowed ${businessSpec.minPitchInches}"–${businessSpec.maxPitchInches}".`,
        'businessPitchInches'
      );
    }
  }

  // 7. Galleys.
  const galleys = sanitizeCount(config.galleyCount);
  if (totalSeats > 0 && galleys < recommendedGalleys) {
    collector.add(
      'INSUFFICIENT_GALLEYS',
      'WARNING',
      `${galleys} galley${galleys === 1 ? '' : 's'} installed but ${recommendedGalleys} are needed to cater ${totalSeats} seats; ` +
        'in-flight service quality will suffer.',
      'galleyCount'
    );
  } else if (galleys > recommendedGalleys + EXCESS_GALLEY_MARGIN) {
    collector.add(
      'EXCESS_GALLEYS',
      'INFO',
      `${galleys} galleys is well above the ${recommendedGalleys} needed; the extra weight increases fuel burn.`,
      'galleyCount'
    );
  }

  // 8. Crew requirement.
  if (totalSeats > 0) {
    collector.add(
      'CABIN_CREW_REQUIREMENT',
      'INFO',
      `This layout needs ${requiredCabinCrew} cabin crew and ${type.crew.cockpitCrew} cockpit crew per flight.`,
      null
    );
  }

  return buildResult(type, collector, {
    floorUnitsUsed,
    totalSeats,
    requiredCabinCrew,
    recommendedGalleys,
  });
}

/** True when at least one ERROR-level issue is present. */
export function hasBlockingIssues(issues: readonly CabinValidationIssue[]): boolean {
  return issues.some((issue) => issue.severity === 'ERROR');
}

/** Filters issues to one severity. */
export function getIssuesBySeverity(
  issues: readonly CabinValidationIssue[],
  severity: CabinIssueSeverity
): readonly CabinValidationIssue[] {
  return issues.filter((issue) => issue.severity === severity);
}

/* -------------------------------------------------------------------------- *
 * Metrics
 * -------------------------------------------------------------------------- */

/**
 * Calculates every derived figure for a cabin on a given aircraft type:
 * floor usage, weight, fuel burn, per-class comfort and price tolerance, and
 * the validation outcome. Safe to call with an invalid configuration — the
 * numbers are sanitised and `isValid` / `issues` explain the problems.
 */
export function calculateCabinMetrics(
  type: AircraftType,
  config: CabinConfiguration
): CabinMetrics {
  const validation = validateCabinConfiguration(type, config);

  const seatsByClass = getSeatsByClass(config);
  const totalSeats = getTotalSeats(config);
  const floorUnitsByClass = getFloorUnitsByClass(config);
  const floorUnitsUsed = getFloorUnitsUsed(config);
  const floorUnitsBudget = type.cabinFloorUnits;
  const usageRatio = floorUnitsBudget > 0 ? floorUnitsUsed / floorUnitsBudget : 1;

  const weightBreakdown = calculateCabinWeight(config);
  const fuel = calculateFuelBurn(type, weightBreakdown.totalKg);

  const comfortByClass = calculateComfortByClass(config, usageRatio);
  const toleranceByClass = calculateToleranceByClass(comfortByClass, seatsByClass);

  return {
    aircraftTypeId: type.id,
    floorUnitsBudget,
    floorUnitsUsed: roundTo(floorUnitsUsed, 2),
    floorUnitsRemaining: roundTo(floorUnitsBudget - floorUnitsUsed, 2),
    totalSeats,
    seatsByClass,
    classRatios: getClassRatios(seatsByClass),
    floorUnitsByClass: buildClassMap((seatClass) => roundTo(floorUnitsByClass[seatClass], 2)),
    cabinWeightKg: roundTo(weightBreakdown.totalKg, 1),
    weightBreakdown: {
      seatsKg: roundTo(weightBreakdown.seatsKg, 1),
      galleysKg: roundTo(weightBreakdown.galleysKg, 1),
      wifiKg: roundTo(weightBreakdown.wifiKg, 1),
      powerKg: roundTo(weightBreakdown.powerKg, 1),
      totalKg: roundTo(weightBreakdown.totalKg, 1),
    },
    weightDeltaKg: roundTo(fuel.weightDeltaKg, 1),
    effectiveFuelBurnKgHr: roundTo(fuel.effectiveFuelBurnKgHr, 1),
    fuelBurnDeltaKgHr: roundTo(fuel.fuelBurnDeltaKgHr, 1),
    comfortByClass: buildClassMap((seatClass) => roundTo(comfortByClass[seatClass], 1)),
    comfortScore: roundTo(seatWeightedAverage(comfortByClass, seatsByClass, 0), 1),
    priceToleranceByClass: buildClassMap((seatClass) => roundTo(toleranceByClass[seatClass], 3)),
    priceToleranceMultiplier: roundTo(seatWeightedAverage(toleranceByClass, seatsByClass, 1), 3),
    requiredCabinCrew: validation.requiredCabinCrew,
    isValid: validation.isValid,
    issues: validation.issues,
  };
}
